import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TdsButton, TdsTable, TdsTableColumn, TdsTableSort, TdsTableRowSelection, TdsTablePagination, TdsTableEditable, TdsTableExpandableRows, TdsField, TdsLoadingSpinner } from '@trv-tds/react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { pencil1_18 } from '@trv-ebus/tds-icons';
import { APP_STRINGS, AUTO_SEARCH_TEXT, ERROR_MESSAGES } from '../../../constants/textConstants';
import { searchConfig } from '../../../config/searchConfig';
import type { SearchResultConfig, SearchColumn, SearchSummaryItem } from '../../../config/searchConfig';
import { getProviderStatusOptions, getCustomerNetworkCodes, getAllPropertyVendorSpecialtyOptions } from '../../../config/updateProviderConfig';
import { clearSelectedRows, setSelectedRows } from '../../../redux/slices/providerSearchSlice';
import type { RootState } from '../../../redux/store';
import { exportVendorsToPDF, exportSelectedVendorsToPDF } from '../../../utils/export/pdfExport';
import ExcelLogo from '../../../assets/microsoft-excel.svg';
import PdfLogo from '../../../assets/pdf-file-icon.svg';
import Mask from '../../../assets/Mask.svg';
import Tag from '../../../assets/Tag.svg'
import { EXTERNAL_ROUTES } from '../../../constants/urlConstants';
import { CONTRACT_VENDOR_SPECIALITY_MAP, CONTRACT_TYPE_OPTIONS } from '../../../constants/providerSearchConstants';
import type { Vendor, PropertyVendor } from '../../../types/providerSearch';
import { formatPhoneForUI } from '../../../services/providerTransformers';

interface SearchResultsProps {
  mode?: 'admin' | 'external';
}

const getVendorId = (vendor: any) => vendor.vendorId || vendor.vendorID || vendor.id;

// Helper function to convert service codes to descriptions
const getServiceCodeDescriptions = async (serviceCodes?: string): Promise<string> => {
  if (!serviceCodes) return '';
  const codes = serviceCodes.split(',').map(code => code.trim()).filter(Boolean);
  try {
    // Always use all specialty options for mapping
    const propertyVendorSpecialtyOptions = await getAllPropertyVendorSpecialtyOptions();
    if (propertyVendorSpecialtyOptions.length === 0) {
      return codes.join(', ');
    }
    const descriptions = codes.map(code => {
      const option = propertyVendorSpecialtyOptions.find(opt => opt.id === code);
      return option ? option.value : code;
    }).filter(Boolean);
    return descriptions.join(', ');
  } catch (error) {
    console.error('❌ [SERVICE CODES] Failed to fetch specialty options:', {
      error,
      serviceCodes,
      timestamp: new Date().toISOString()
    });
    return codes.join(', ');
  }
};

// Helper function to convert status code to full text
const getStatusText = async (status?: string): Promise<string> => {
  if (!status) return '';
  try {
    const statusOptions = await getProviderStatusOptions();
    const statusOption = statusOptions.find((opt: any) => opt.id === status);
    return statusOption ? statusOption.value : status;
  } catch (error) {
    console.error('❌ [STATUS TEXT] Failed to fetch status options:', {
      error,
      status,
      timestamp: new Date().toISOString()
    });
    // Return the status code as-is on error
    return status;
  }
};



const SearchResults: React.FC<SearchResultsProps> = ({ mode }) => {
  // Redux state
  const {
    searchResults,
    formData,
    selectedRows,
    externalSearchType,
    currentSearchCategory, // Correct property name from Redux state
    totalCount,
    currentPage: apiCurrentPage,
    pageSize: apiPageSize,
  } = useSelector((state: RootState) => state.providerSearch);

  const dispatch = useDispatch();
  const location = useLocation();

  // Local state
  const [isProcessing, setIsProcessing] = useState(false);
  const getInitialSortConfig = (externalSearchType?: string): { colId: string | number; order: 'ascending' | 'descending' } => {
    if (externalSearchType === 'name') {
      return { colId: 'vendorName', order: 'ascending' as const };
    }
    // Default and 'local' case
    return { colId: 'approximateMiles', order: 'ascending' as const };
  };

  const [sortConfig, setSortConfig] = useState<{ colId: string | number; order: 'ascending' | 'descending' }>(
    getInitialSortConfig(externalSearchType)
  );
  const [processedResults, setProcessedResults] = useState<any[]>([]);
  const [liveMessage, setLiveMessage] = useState<string>('');
  const [paginationAnnouncement, setPaginationAnnouncement] = useState<string>('');
  const [selectionAnnouncement, setSelectionAnnouncement] = useState<string>('');
  const tableRef = useRef<HTMLDivElement>(null);
  const savedScrollPosition = useRef<number>(0);

  // Constants
  const itemsPerPage = 10;
  const rowsPerPage: (number | string)[] = [10, 25, 50, 'ALL'];

  // Prevent scroll to top on pagination changes
  useEffect(() => {
    // Restore scroll position after results update
    if (savedScrollPosition.current > 0) {
      const scrollY = savedScrollPosition.current;
      // Use setTimeout to ensure it runs after browser's scroll
      setTimeout(() => {
        window.scrollTo({ top: scrollY, behavior: 'instant' });
      }, 0);
    }
  }, [searchResults]);

  // Reset sort when search type or category changes
  useEffect(() => {
    setSortConfig(getInitialSortConfig(externalSearchType));
  }, [currentSearchCategory, externalSearchType]);

  // Listen for sort events
  useEffect(() => {
    const tableElement = tableRef.current;
    if (!tableElement) return;

    const handleSortEvent = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        const { colIndex, colName, ascending } = customEvent.detail;
        setSortConfig({
          colId: colName || colIndex, // Prefer colName over colIndex
          order: ascending ? 'ascending' : 'descending'
        });
      }
    };

    tableElement.addEventListener('tdsSortOnColumn', handleSortEvent);
    return () => {
      tableElement.removeEventListener('tdsSortOnColumn', handleSortEvent);
    };
  }, []);

  // Determine mode and permissions
  const isExternalMode = mode === 'external' ||
    (!mode && location.pathname.startsWith('/ext/'));
  // const { isAdmin } = useIsAdmin();
  // For contract search, always show PDF (both admin and external); otherwise PDF for external, Excel for admin
  const shouldShowEmailButton = isExternalMode;

  // Helper to convert approval status codes (string or array) to display text
  const getApprovalStatusText = useCallback(async (
    approvalStatus?: string | string[]
  ): Promise<string> => {
    if (!approvalStatus) return '';
    try {
      // Admin Property should use Customer Network Codes; others use Provider Status options
      const useCustomerNetworkCodes = !isExternalMode && currentSearchCategory === 'property';
      const statusOptions = useCustomerNetworkCodes
        ? await getCustomerNetworkCodes()
        : await getProviderStatusOptions();

      // Normalize to array of codes
      const codes: string[] = Array.isArray(approvalStatus)
        ? approvalStatus
        : approvalStatus.split(',').map((c) => c.trim()).filter(Boolean);

      const descriptions = codes
        .map((code) => {
          const match = statusOptions.find((opt: any) => opt.id === code);
          return match ? match.value : code;
        })
        .filter(Boolean);

      return descriptions.join(', ');
    } catch (error) {
      console.error('❌ [APPROVAL STATUS] Failed to fetch status options:', {
        error,
        approvalStatus,
        isExternalMode: isExternalMode,
        category: currentSearchCategory,
        timestamp: new Date().toISOString()
      });
      // Fallback to returning codes as-is on error
      const codes: string[] = Array.isArray(approvalStatus)
        ? approvalStatus
        : approvalStatus.split(',').map((c) => c.trim()).filter(Boolean);
      return codes.join(', ');
    }
  }, [isExternalMode, currentSearchCategory]);


  // Get configuration based on search category and mode
  const getConfig = (): SearchResultConfig | undefined => {

    if (!currentSearchCategory || !searchConfig[currentSearchCategory]) {
      return undefined;
    }

    const categoryConfig = searchConfig[currentSearchCategory];
    // Type guard for config object
    if (!isExternalMode) {
      if (categoryConfig && typeof categoryConfig === 'object' && 'admin' in categoryConfig) {
        return (categoryConfig as any).admin;
      }
      return undefined;
    } else {
      if (categoryConfig && typeof categoryConfig === 'object' && 'external' in categoryConfig) {
        const externalConfig = (categoryConfig as any).external;
        const searchType = externalSearchType || 'local';
        if (externalConfig && typeof externalConfig === 'object' && searchType in externalConfig) {
          return externalConfig[searchType];
        }
      }
      return undefined;
    }
  };

  const config = getConfig();

  useEffect(() => {
    const processResults = async () => {
      if (!searchResults || !searchResults.length || !config) {
        setProcessedResults([]);
        setLiveMessage('No results found.');
        return;
      }

      try {
        const results = await Promise.all(searchResults.map(async (vendor, index) => {
          const vendorId = getVendorId(vendor);
          // Create unique ID using multiple fields to ensure uniqueness
          const uniqueId = `${vendorId || 'no-id'}-${vendor.name || 'no-name'}-${vendor.address?.street || (vendor as any).address || 'no-addr'}-${index}`;

          const baseResult: any = {
            ...vendor,
            // Always show DBA (doingBusinessAs) if present, else vendorName
            vendorName: ('dba' in vendor && vendor.dba ? vendor.dba : vendor.name || ''),
            vendorId: vendorId,
            // TDS table uses 'id' field to identify rows
            id: uniqueId,
            uniqueRowId: uniqueId,
            address: (
              <span>
                {vendor.address?.street || ''}<br />
                {vendor.address?.city || ''}, {vendor.address?.state || ''} {vendor.address?.zipCode || ''}
              </span>
            ),
            // Add sortable text version of address for sorting
            addressSortValue: `${vendor.address?.street || ''} ${vendor.address?.city || ''} ${vendor.address?.state || ''} ${vendor.address?.zipCode || ''}`.trim(),
          };

          if ('phone' in vendor) {
            baseResult.phone = formatPhoneForUI((vendor as any).phone || '');
          }


          // Normalize provider status for display across categories (Auto default)
          const statusCode = (vendor as any).providerStatusCd || (vendor as any).status || '';
          baseResult.status = await getStatusText(statusCode);

          // Add distance for local searches - now provided by API
          if (config?.columns.some((col: SearchColumn) => col.field === 'approximateMiles')) {
            // Use approximateMiles from API response if available (not part of type definition)
            const vendorWithMiles = vendor as any;
            if (vendorWithMiles.approximateMiles !== undefined && vendorWithMiles.approximateMiles !== null) {
              baseResult.approximateMiles = typeof vendorWithMiles.approximateMiles === 'number'
                ? vendorWithMiles.approximateMiles.toFixed(1)
                : vendorWithMiles.approximateMiles;
            }

          }

          // Add category-specific fields
          if (currentSearchCategory === 'property') {
            // Normalize property vendor fields for display based on searchConfig
            baseResult.vendorName = (vendor as any).key === true ? (
              <span className="tds-d-flex tds-align-items-center" style={{ gap: '0.75rem' }}>
                <span>{'dba' in vendor && vendor.dba ? vendor.dba : vendor.name || ''}</span>
                <img src={Tag} alt="Preferred Vendor" />
              </span>
            ) : ('dba' in vendor && vendor.dba ? vendor.dba : vendor.name || '');
            baseResult.vendorId = getVendorId(vendor);
            baseResult.address = (
              <span>
                {vendor.address?.street || ''}<br />
                {[vendor.address?.city, vendor.address?.state].filter(Boolean).join(', ')} {vendor.address?.zipCode || ''}
              </span>
            );
            baseResult.addressSortValue = `${vendor.address?.street || ''} ${vendor.address?.city || ''} ${vendor.address?.state || ''} ${vendor.address?.zipCode || ''}`.trim();
            baseResult.phone = 'phone' in vendor ? formatPhoneForUI(vendor.phone || '') : '';
            baseResult.status = 'status' in vendor ? await getStatusText(vendor.status) || '' : '';
            // Render approval status values line-by-line instead of continuous text
            if ('approvalStatus' in vendor) {
              const approvalStatusText = await getApprovalStatusText((vendor as any).approvalStatus);
              baseResult.approvalStatus = approvalStatusText
                ? (
                  <span className="tds-d-flex tds-flex-column">
                    {approvalStatusText.split(',').map((text: string, idx: number, arr: string[]) => (
                      <span key={idx}>
                        {text.trim()}
                        {idx < arr.length - 1 ? ',' : ''}
                      </span>
                    ))}
                  </span>
                )
                : '';
            } else {
              baseResult.approvalStatus = '';
            }
            // For property vendors, show service code descriptions from serviceCodes field
            const propertyVendor = vendor as any;
            // Handle serviceCodes as either string or array
            const serviceCodesValue = Array.isArray(propertyVendor.serviceCodes)
              ? propertyVendor.serviceCodes.join(', ')
              : (propertyVendor.serviceCodes || '');
            const serviceCodeDescriptions = serviceCodesValue && typeof serviceCodesValue === 'string' && serviceCodesValue.trim() !== ''
              ? await getServiceCodeDescriptions(serviceCodesValue)
              : '';
            // Set both field names for compatibility with different search configs
            // baseResult.vendorSpeciality = serviceCodeDescriptions;
            if (currentSearchCategory === 'property' || (currentSearchCategory === 'propertyClaim')) {
              // For property: show vendorSpeciality field & exclude it for propertyClaim
              baseResult.vendorSpeciality = serviceCodeDescriptions;
            }
            baseResult.vendorSpecializations = serviceCodeDescriptions;            
            const rawMilesProp = (vendor as any).approximateMiles;
            baseResult.approximateMilesSortValue = typeof rawMilesProp === 'number' ? rawMilesProp : -1;
            baseResult.approximateMiles = (rawMilesProp === undefined || rawMilesProp === null)
              ? 'N/A'
              : `${Number(rawMilesProp).toFixed(1)} miles`;
            baseResult.notes = 'notes' in vendor ? vendor.notes || '' : '';
          }
          // Mark selected based on Redux selectedRows using id/uniqueRowId
          baseResult.isSelected = selectedRows.some(
            (sr: any) => sr.id === uniqueId || sr.uniqueRowId === uniqueId
          );

          return baseResult;
        }));

        setProcessedResults(results);
        setLiveMessage(`${results.length} result${results.length === 1 ? '' : 's'} found.`);
      } catch (error) {
        console.error('❌ [PROCESS RESULTS] Failed to process search results:', {
          error,
          resultsCount: searchResults?.length,
          category: currentSearchCategory,
          timestamp: new Date().toISOString()
        });
        // Set empty results on error to prevent UI crashes
        setProcessedResults([]);
      }
    };

    processResults();
  }, [searchResults, config, currentSearchCategory, selectedRows, getApprovalStatusText]);

  // Early return for no search results - MUST be after all hooks
  if (!searchResults) {
    return null;
  }


  const handlePageChange = (page: number) => {
    // If we are in ALL mode (pageSize 0), ignore page changes
    if (apiPageSize === 0) {
      return;
    }
    // Clear selected rows when changing page
    dispatch(clearSelectedRows());

    savedScrollPosition.current =
      window.pageYOffset || document.documentElement.scrollTop;

    // Trigger API call with new page number
    if (formData) {
      const base = buildSearchPayload();
      const payload = {
        ...formData,
        currentSearchCategory, // Include search category for proper API routing
        ...base,
        page: page.toString(),
        pageSize: (apiPageSize || itemsPerPage).toString(),
      } as any;
      dispatch({
        type: 'providerSearch/searchRequest',
        payload,
      });
    }
  };

  const buildSearchPayload = () => {
    if (!formData) return null;

    const payload: any = {
      ...formData,
      currentSearchCategory,
    };

    // Ensure vendorSpecialtyTypeCode is present based on formData.vendorSpecialty
    const vendorSpecialty: any = (formData as any).vendorSpecialty;
    if (vendorSpecialty && !payload.vendorSpecialtyTypeCode) {
      if (Array.isArray(vendorSpecialty)) {
        payload.vendorSpecialtyTypeCode = vendorSpecialty.map(String).join(',');
      } else if (typeof vendorSpecialty === 'string') {
        payload.vendorSpecialtyTypeCode = vendorSpecialty;
      }
    }

    // Internal vs admin
    if (isExternalMode) {
      // externalSearchType is already in Redux
      payload.searchType = externalSearchType || 'local';
    }

    return payload;
  };

  const handleRowSelection = (e: CustomEvent) => {
    const newSelectedRows = e.detail.selectedRowItems || [];

    // Map back to raw vendor data (without JSX) for Redux storage
    const rawSelectedVendors = newSelectedRows.map((selectedRow: any) => {
      // Extract the index from the uniqueId (last part after all dashes)
      const idParts = selectedRow.id?.split('-') || [];
      const originalIndex = parseInt(idParts[idParts.length - 1]);

      if (!isNaN(originalIndex) && originalIndex >= 0 && searchResults[originalIndex]) {
        return {
          ...searchResults[originalIndex],
          uniqueRowId: selectedRow.id,
          id: selectedRow.id
        };
      }

      // Fallback: create a serializable version without JSX
      //eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { address, vendorName, approvalStatus, ...rest } = selectedRow;
      return {
        ...rest,
        name: typeof vendorName === 'string' ? vendorName : rest.name,
        uniqueRowId: selectedRow.id,
        id: selectedRow.id
      };
    });

    dispatch(setSelectedRows(rawSelectedVendors));
    const count = newSelectedRows.length;
    setSelectionAnnouncement(
      count === 0 ? 'No rows selected.' : `${count} row${count === 1 ? '' : 's'} selected.`
    );
  };

  const handlePostListClick = () => {
    setIsProcessing(true);
    const category = currentSearchCategory || 'vendor';

    setTimeout(async () => {
      try {
        // For contract category or external mode, always export to PDF
        if (shouldShowEmailButton) {
          // External users or Contract search - Export to PDF
          if (selectedRows.length > 0) {
            // selectedRows now contains original vendor data (from Redux)
            // Find the matching processed result that has the formatted miles
            const selectedVendorsWithMiles = selectedRows.map(selectedRow => {
              const processedVendor = processedResults.find(
                pVendor => pVendor.id === selectedRow.id || pVendor.uniqueRowId === selectedRow.id
              );

              // Return vendor with formatted data from processed results
              return {
                ...selectedRow,
                phone: processedVendor?.phone || selectedRow.phone || '',
                approximateMiles: processedVendor?.approximateMiles || ''
              };
            });

            exportSelectedVendorsToPDF(selectedVendorsWithMiles as unknown as (Vendor | PropertyVendor)[], {
              title: getSearchResultsHeader() || '',
              filename: `selected-${category}-vendors.pdf`,
              isLocalSearch: externalSearchType === 'local'
            });
          } else {
            // For exporting all results, add formatted miles and phone
            const allVendorsWithMiles = searchResults.map(originalVendor => {
              const processedVendor = processedResults.find(
                pVendor => getVendorId(pVendor) === getVendorId(originalVendor)
              );

              return {
                ...originalVendor,
                phone: processedVendor?.phone || originalVendor.phone || '',
                approximateMiles: processedVendor?.approximateMiles || ''
              };
            });

            exportVendorsToPDF(allVendorsWithMiles as unknown as (Vendor | PropertyVendor)[], {
              title: getSearchResultsHeader() || '',
              filename: `${category}-search-results.pdf`,
              isLocalSearch: externalSearchType === 'local'
            });
          }
        }
      } catch (error) {
        console.error('Error processing request:', error);
      } finally {
        setIsProcessing(false);
      }
    });
  };


  // Generate search results header
  const getSearchResultsHeader = () => {

    // external mode logic
    const count = totalCount || searchResults.length || 0;
    return `${AUTO_SEARCH_TEXT.SEARCH_RESULTS}: " ${count} Vendors matching your search criteria "`;
  };

  const effectiveTotalItems = totalCount || searchResults.length;

  const effectivePageSize =
    apiPageSize === 0
      ? effectiveTotalItems || itemsPerPage
      : apiPageSize || itemsPerPage;

  const effectivePage =
    apiPageSize === 0
      ? 1
      : apiCurrentPage || 1;


  return (
    <div className="tds-container tds-padding-top-small tds-padding-left-xl">
      <div className="tds-row">
        <div className="tds-col-12">
          <hr className='tds-margin-top-large'></hr>
          {processedResults.length > 0 && config ? (
            <div className="tds-margin-top-medium">
              <TdsField>
                <div className='tds-row tds-justify-content-between'>
                  <h3
                    className="tds-col-7 s-body-large"
                    tabIndex={0}
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    {getSearchResultsHeader()}
                  </h3>
                  <div id="results-status" className="tds-sr-only" role="status" aria-live="polite" aria-atomic="true">{liveMessage} {paginationAnnouncement} {selectionAnnouncement}</div>

                  {/* Render summary action buttons from config.summary */}
                  {config && config.summary ? (
                    <div className="tds-col-auto tds-d-flex tds-gap-8">
                      <TdsButton
                        className="tds-col-auto"
                        variant="secondary"
                        onClick={handlePostListClick}
                        disabled={selectedRows.length === 0}
                        aria-label={`Download selected vendors as PDF (${selectedRows.length} selected)`}
                        aria-disabled={selectedRows.length === 0 ? true : undefined}
                      >
                        {shouldShowEmailButton && (
                          <img
                            src={PdfLogo}
                            alt="PDF"
                            slot="icon-last"
                            width="12"
                            height="12"
                            style={{
                              filter: selectedRows.length === 0
                                ? "brightness(0) saturate(100%) invert(72%) sepia(6%) saturate(218%) hue-rotate(169deg) brightness(92%) contrast(88%)"
                                : "invert(15%) sepia(95%) saturate(6932%) hue-rotate(358deg) brightness(95%) contrast(112%)"
                            }}
                          />
                        )}
                        {!shouldShowEmailButton ? APP_STRINGS.EXPORT : "PRINT SELECTED"}
                      </TdsButton>
                    </div>
                  ) : null}


                </div>
              </TdsField>
              {/* Property-specific key and text rendering */}
              {currentSearchCategory === 'property' && config && (
                <div className="tds-margin-top-medium tds-margin-bottom-small">
                  {/* Key section using only TDS classes */}
                  {config.key && (
                    <div className='tds-row tds-margin-bottom-large'>
                      <div className='tds-col-12'>
                        <p className='tds-weight-normal'>
                          <span className='tds-text-transform-uppercase'>Key: </span>
                          <span className='tds-margin-left-xs tds-margin-right-xs' />
                          <img src={Tag} alt="" aria-hidden="true" className="tds-margin-right-xs" />
                          Preferred Vendor
                        </p>
                      </div>
                    </div>
                  )}
                  {/* Contact information section */}
                  {config.text && processedResults.length > 0 && (() => {
                    // Extract contact info from first vendor or use default
                    return (
                      <div className="tds-row tds-margin-bottom-small">
                        <p
                          className="tds-col-12 tds-small tds-margin-0"
                          dangerouslySetInnerHTML={{ __html: config.text }}
                        />
                      </div>
                    );
                  })()}
                </div>
              )}
              {config.helpText && (
                <div className='tds-row tds-margin-top-small'>
                  <em><p className='tds-col-12 tds-small'>{config.helpText}</p></em>
                </div>
              )}
              <div ref={tableRef} tabIndex={-1}>
                <TdsTable
                  key={`${apiCurrentPage}-${apiPageSize}`}
                  rowItems={processedResults}
                  fullWidth
                  aria-label={getSearchResultsHeader() || 'Search Results'}
                  aria-describedby="results-status"
                >
                  <TdsTableColumn rowSelection></TdsTableColumn>
                  {/* {currentSearchCategory === 'property' && (
                  <TdsTableColumn row-expansion></TdsTableColumn>
                )} */}
                  {config.columns.map((col: SearchColumn, idx: number) => {
                    if (col.type === 'actions') {
                      return <TdsTableColumn key={idx} itemPath="actions">
                        <span className="tds-d-flex tds-align-items-center tds-col-12 tds-text-primary">
                          <span dangerouslySetInnerHTML={{
                            __html: pencil1_18.svg()
                          }} style={{ color: '#0066cc' }} />
                        </span>

                      </TdsTableColumn>
                    }
                    // Only Vendor Name, Vendor ID, Address, Approximate Miles, and Status columns are sortable
                    const isSortable = col.field === 'vendorName' || col.field === 'vendorId' || col.field === 'address' || col.field === 'approximateMiles' || col.field === 'status';
                    const itemPath: string = col.field ? col.field : '';
                    if (isSortable) {
                      return (
                        <TdsTableColumn
                          key={idx}
                          itemPath={itemPath}
                          // editable={!isExternalMode && col.field !== 'vendorId'}
                          sortable={true}
                          {...(col.field !== 'approximateMiles' ? { sortAscendingText: 'A to Z', sortDescendingText: 'Z to A' } : {})}
                          {...(col.field === 'approximateMiles' ? { sortItemPath: 'approximateMilesSortValue' } : {})}
                          {...(col.field === 'address' && { sortItemPath: 'addressSortValue' })}
                        >
                          {col.label}
                        </TdsTableColumn>
                      );
                    } else {
                      return (
                        <TdsTableColumn
                          key={idx}
                          itemPath={itemPath}
                          // editable={!isExternalMode && col.field !== 'vendorId'}
                          sortable={false}
                        >
                          {col.label}
                        </TdsTableColumn>
                      );
                    }
                  })}
                  <TdsTableSort
                    sortMode="simple"
                    sortedBy={sortConfig}
                    ignoreRowUpdate
                  ></TdsTableSort>

                  <TdsTableRowSelection
                    selected-path="isSelected"
                    selectedRows={selectedRows}
                    onTdsRowSelection={(e: any) => handleRowSelection(e)}
                  ></TdsTableRowSelection>

                </TdsTable>
                <nav aria-label="Results pagination">
                  <TdsTablePagination
                    key={`pagination-$${apiCurrentPage}-$${totalCount}`}
                    pageSizeOptions={rowsPerPage}
                    showRecordsDetail={true}
                    page={effectivePage}
                    pageSize={effectivePageSize}
                    totalItems={effectiveTotalItems}
                    onTdsPageChanged={(e: any) => {

                      // If we are in "ALL" mode (pageSize 0), ignore page changes
                      if (apiPageSize === 0) {
                        return;
                      }

                      if (e.detail.userAction !== false) {
                        setPaginationAnnouncement(`Page ${e.detail.page} selected.`);
                        handlePageChange(e.detail.page);
                      }
                    }}
                    onTdsPageSizeChanged={(e: any) => {
                      const newPageSize = e.detail.pageSize as number; // TDS will give 0 for "ALL"
                      if (e.detail.userAction !== false) {
                        // Clear selected rows when changing page size
                        dispatch(clearSelectedRows());
                        // Save current scroll position before page size change
                        savedScrollPosition.current =
                          window.pageYOffset || document.documentElement.scrollTop;

                        if (formData) {

                          // When user selects "ALL", TDS sets pageSize to 0.
                          // Decide what your API expects: 0 or -1. You said -1, so map it.
                          const apiPageSizeToSend =
                            newPageSize === 0 ? '-1' : newPageSize.toString();

                          const base = buildSearchPayload();

                          dispatch({
                            type: 'providerSearch/searchRequest',
                            payload: {
                              ...formData,
                              currentSearchCategory,
                              ...base,
                              page: '1',
                              pageSize: apiPageSizeToSend,
                            },
                          });
                          setPaginationAnnouncement(
                            newPageSize === 0 ? 'Showing all results.' : `Page size set to ${newPageSize}.`
                          );
                        }
                      }
                    }}
                  />
                </nav>
              </div>
            </div>
          ) : (
            <div className="tds-margin-top-medium">
              <TdsField>
                <div className='tds-row tds-justify-content-between'>
                  <h3
                    className="tds-col-7 s-body-large"
                    tabIndex={0}
                    aria-live='polite'
                    aria-atomic='true'
                  >
                    {getSearchResultsHeader()}
                  </h3>
                </div>
                <div className='tds-margin-top-xxl tds-row tds-justify-content-center'>
                  <h3 className='tds-h3' tabIndex={0}>{APP_STRINGS.NO_RESULTS_FOUND}</h3>
                </div>
                <div className='tds-row tds-justify-content-center tds-margin-top-m'>
                  <p className='tds-small tds-text-muted'>
                    {externalSearchType === 'local'
                      ? 'Try adjusting your search criteria or expanding your search distance.'
                      : 'Try adjusting your search terms, city, or state selections.'
                    }
                  </p>
                </div>
                <div className="tds-sr-only" role="status" aria-live="polite" aria-atomic="true">No results found.</div>
              </TdsField>
            </div>
          )}

          {isProcessing && (
            <TdsLoadingSpinner
              label='Generating pdf...'
              label-displayed
              overlay
              centered
              size="xxx-large"
            ></TdsLoadingSpinner>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchResults;